import ObjectID from "bson-objectid";
import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { filterActiveTenants } from "./../../utils/tenantUtils";

// available room types (canonical values)
const ROOM_TYPES = [
    "Kitchen",
    "Living Room",
    "Reception Room",
    "Bathroom",
    "WC",
    "Bedroom",
    "Hallway",
    "Landing",
    "Dining Room",
    "Utility Room",
    "Office",
    "Conservatory"
];

// normalize an incoming roomType to one of the canonical ROOM_TYPES (case-insensitive).
// returns canonical type if matched, otherwise returns the original string trimmed.
const normalizeRoomType = (rt) => {
    if (rt === null || rt === undefined) return rt;
    const s = String(rt).trim();
    const found = ROOM_TYPES.find(t => t.toLowerCase() === s.toLowerCase());
    return found ?? s;
};

export const PropertyRoomsTab = ({
    propertyDocument,
    isHMO,
    onRoomsChange,
    tenants: propTenants,
    tenantsLoading = false,
    onTenantsChange // optional callback to persist tenant updates (active tenancy room number)
}) => {
    // use tenants passed from parent first, otherwise propertyDocument.tenants
    const tenants = propTenants ?? propertyDocument?.tenants ?? [];

    // only show active tenants in assignment dropdowns
    const activeTenants = filterActiveTenants(tenants);

    // helper to get a display name for a tenant
    const tenantDisplayName = (t) => {
        if (!t) return "";
        return t.displayName || t.name || t.fullName || String(t);
    };

    // helper to read tenancy room number (various shapes)
    const tenantTenancyRoomNumber = t => t?.activeTenancy?.roomNumber ?? undefined;

    // normalize tenant options (include tenancy room number in label when present)
    const tenantOptions = activeTenants.map(t => {
        const roomNum = tenantTenancyRoomNumber(t);
        const label = roomNum ? `${tenantDisplayName(t)} (room ${roomNum})` : tenantDisplayName(t);
        return { id: t?.id ?? t?.tenantId ?? t?.key, label, roomNum: roomNum ?? undefined };
    });

    // local rooms state (syncs from propertyDocument.property.rooms)
    const [rooms, setRooms] = useState(propertyDocument?.property?.rooms ?? []);
    const [selectedType, setSelectedType] = useState("");
    const [selectedRoomNumber, setSelectedRoomNumber] = useState(null); // free numeric, can be null when not provided
    const [selectedTenantId, setSelectedTenantId] = useState(""); // for bedrooms in HMO

    // validation state
    const [roomErrors, setRoomErrors] = useState({}); // { [roomId]: message }
    const [addError, setAddError] = useState(null);

    useEffect(() => {
        const incoming = propertyDocument?.property?.rooms ?? [];
        // normalize roomType on load so UI sees canonical values
        const normalized = Array.isArray(incoming) ? incoming.map(r => {
            // ensure canonical id exists
            const id = r?.id ?? r?._id ?? r?.key ?? r?.roomId ?? new ObjectID().toHexString();
            return { ...r, id, roomType: normalizeRoomType(r?.roomType) };
        }) : [];
        setRooms(normalized);
    }, [propertyDocument?.property?.rooms]);

    useEffect(() => {
        // reset bedroom-specific inputs when type changes
        if (selectedType !== "Bedroom") {
            setSelectedRoomNumber(null);
            setSelectedTenantId("");
        }
    }, [selectedType]);

    // find tenant helper
    const findTenantById = id => tenants.find(t => {
        const tid = t?.id ?? t?.tenantId ?? t?.key;
        return String(tid) === String(id);
    });

    const navigate = useNavigate();

    const getPropertyId = () => {
        // propertyDocument may be shaped as { id, property: { ... } } or similar
        return propertyDocument?.id ?? propertyDocument?._id ?? propertyDocument?.property?.id ?? propertyDocument?.property?.propertyId ?? null;
    };

    const resolveRoomId = (room) => {
        if (!room) return null;
        return room.id ?? room._id ?? room.key ?? room.roomId ?? null;
    };

    const goToRoom = (roomOrId) => {
        const pid = getPropertyId();
        if (!pid) return;
        const rid = typeof roomOrId === 'object' ? resolveRoomId(roomOrId) : roomOrId;
        if (!rid) {
            console.warn('Could not determine room id for navigation', roomOrId);
            return;
        }
        navigate(`/property/${pid}/room/${rid}`);
    };

    // validate a room number: must be a positive integer (no upper limit)
    const isValidRoomNumber = (v) => {
        if (v === null || v === undefined) return false;
        const s = String(v).trim();
        if (s === "") return false;
        return /^\d+$/.test(s) && Number(s) > 0;
    };

    const setRoomError = (roomId, message) => {
        setRoomErrors(prev => {
            const copy = { ...prev };
            if (message) copy[roomId] = message;
            else delete copy[roomId];
            return copy;
        });
    };

    const clearAllAddErrors = () => setAddError(null);

    // check duplicate room number across rooms, exclude optional roomId
    const isDuplicateNumber = (number, excludeRoomId = null) => {
        if (!isValidRoomNumber(number)) return false;
        return rooms.some(r => r.id !== excludeRoomId && r.roomType === "Bedroom" && String(r.roomNumber) === String(number));
    };

    const persistRooms = (updatedRooms) => {
        // ensure stored roomType is canonical before persisting
        const normalized = Array.isArray(updatedRooms) ? updatedRooms.map(r => ({
            ...r,
            id: r?.id ?? r?._id ?? r?.key ?? r?.roomId ?? new ObjectID().toHexString(),
            roomType: normalizeRoomType(r?.roomType)
        })) : [];
        setRooms(normalized);
        if (typeof onRoomsChange === "function") onRoomsChange(normalized);
    };

    const persistTenants = (updatedTenants) => {
        if (typeof onTenantsChange === "function") onTenantsChange(updatedTenants);
    };

    const addRoom = () => {
        clearAllAddErrors();
        if (!selectedType) return;

        // prevent assigning a tenant that's already assigned to another bedroom (HMO only)
        if (isHMO && selectedTenantId) {
            const alreadyAssigned = rooms.find(r => r.tenantId && String(r.tenantId) === String(selectedTenantId));
            if (alreadyAssigned) {
                setAddError("That tenant is already assigned to another bedroom.");
                return;
            }
        }

        // for bedrooms:
        // - in HMO: require a valid room number OR a selected tenant (tenant may provide number)
        // - in single lets: require a valid room number only
        if (selectedType === "Bedroom") {
            if (isHMO) {
                if (!isValidRoomNumber(selectedRoomNumber) && !selectedTenantId) return;
                const duplicate = isValidRoomNumber(selectedRoomNumber) && isDuplicateNumber(selectedRoomNumber);
                if (duplicate) {
                    setAddError("That room number is already used.");
                    return;
                }
            } else {
                if (!isValidRoomNumber(selectedRoomNumber)) return;
                const duplicate = isValidRoomNumber(selectedRoomNumber) && isDuplicateNumber(selectedRoomNumber);
                if (duplicate) {
                    setAddError("That room number is already used.");
                    return;
                }
            }
        }

        // determine roomNumber to store
        let roomNumberToStore = undefined;
        if (selectedType === "Bedroom") {
            if (isValidRoomNumber(selectedRoomNumber)) {
                roomNumberToStore = Number(selectedRoomNumber);
            } else if (isHMO && selectedTenantId) {
                const t = findTenantById(selectedTenantId);
                const tn = tenantTenancyRoomNumber(t);
                if (tn !== undefined) {
                    if (isDuplicateNumber(tn)) {
                        setAddError("Tenant's tenancy room number conflicts with an existing room.");
                        return;
                    }
                    roomNumberToStore = Number(tn);
                }
            }
        }

        const newRoom = {
            id: new ObjectID().toHexString(),
            roomType: normalizeRoomType(selectedType),
            ...(selectedType === "Bedroom" ? { roomNumber: roomNumberToStore !== undefined ? roomNumberToStore : null } : {}),
            tenantId: (isHMO && selectedType === "Bedroom" && selectedTenantId) ? selectedTenantId : null
        };
        const updated = [...rooms, newRoom];
        persistRooms(updated);

        // if we assigned a tenant and have a room number, update that tenant's activeTenancy.roomNumber
        if (isHMO && selectedType === "Bedroom" && selectedTenantId && roomNumberToStore !== undefined) {
            const updatedTenants = tenants.map(t => {
                const tid = t?.id ?? t?.tenantId ?? t?.key;
                if (String(tid) !== String(selectedTenantId)) return t;
                const clone = { ...t };
                clone.activeTenancy = { ...(clone.activeTenancy ?? {}), roomNumber: Number(roomNumberToStore) };
                return clone;
            });
            persistTenants(updatedTenants);
        }

        // reset inputs
        setSelectedType("");
        setSelectedRoomNumber(null);
        setSelectedTenantId("");
    };

    const removeRoom = (id) => {
        const updated = rooms.filter(r => r.id !== id);
        persistRooms(updated);
    };

    const changeRoomTenant = (roomId, tenantId) => {
        if (!isHMO) return; // per-room tenant assignment only applies to HMOs
        // when assigning tenant to existing bedroom:
        // - set room.tenantId
        // - if room has roomNumber, update tenant.activeTenancy.roomNumber to match
        // - if tenant has tenancy room number and room has no roomNumber, set room.roomNumber from tenant
        const tenant = tenantId ? findTenantById(tenantId) : null;
        const tenantRoomNum = tenant ? tenantTenancyRoomNumber(tenant) : undefined;

        // prevent assigning tenant whose tenancy roomNumber conflicts with another room (not this room)
        if (tenantRoomNum !== undefined) {
            // if some other room already uses that number and it's not this room, block
            if (isDuplicateNumber(tenantRoomNum, roomId)) {
                setRoomError(roomId, "Tenant's tenancy room number conflicts with another room.");
                return;
            }
        }

        // prevent assigning the same tenant to multiple bedrooms
        if (tenantId) {
            const alreadyAssigned = rooms.find(r => r.id !== roomId && r.tenantId && String(r.tenantId) === String(tenantId));
            if (alreadyAssigned) {
                setRoomError(roomId, "That tenant is already assigned to another bedroom.");
                return;
            }
        }

        const updatedRooms = rooms.map(r => {
            if (r.id !== roomId) return r;
            const updatedRoom = { ...r, tenantId: tenantId || null };
            if (r.roomType === "Bedroom") {
                // if tenant has a room number and room has no valid number, prefer tenant's
                if (!isValidRoomNumber(r.roomNumber) && tenantRoomNum !== undefined) {
                    updatedRoom.roomNumber = Number(tenantRoomNum);
                }
            }
            return updatedRoom;
        });

        // clear any previous error for this room
        setRoomError(roomId, null);
        persistRooms(updatedRooms);

        // if room has a valid roomNumber and tenant assigned, update tenant tenancy to match
        const room = updatedRooms.find(r => r.id === roomId);
        if (room && tenantId && isValidRoomNumber(room.roomNumber)) {
            const updatedTenants = tenants.map(t => {
                const tid = t?.id ?? t?.tenantId ?? t?.key;
                if (String(tid) !== String(tenantId)) return t;
                const clone = { ...t };
                clone.activeTenancy = { ...(clone.activeTenancy ?? {}), roomNumber: Number(room.roomNumber) };
                return clone;
            });
            persistTenants(updatedTenants);
        }
    };

    // update room number for an existing bedroom and mirror change to tenant.activeTenancy if assigned
    const updateRoomNumber = (roomId, newNumberStr) => {
        const newNumberRaw = newNumberStr === "" ? "" : String(newNumberStr).trim();
        if (newNumberRaw === "") {
            // clear the number on room (store null to be explicit)
            const updatedRoomsClear = rooms.map(r => r.id === roomId ? { ...r, roomNumber: null } : r);
            setRoomError(roomId, null);
            persistRooms(updatedRoomsClear);
            return;
        }
        if (!isValidRoomNumber(newNumberRaw)) return;

        // prevent duplicate with other rooms
        if (isDuplicateNumber(newNumberRaw, roomId)) {
            setRoomError(roomId, "That room number is already used by another room.");
            return;
        }

        const updatedRooms = rooms.map(r => r.id === roomId ? { ...r, roomNumber: Number(newNumberRaw) } : r);
        setRoomError(roomId, null);
        persistRooms(updatedRooms);

        // if this room has an assigned tenant, update that tenant's tenancy room number
        const room = updatedRooms.find(r => r.id === roomId);
        if (room && room.tenantId) {
            const updatedTenants = tenants.map(t => {
                const tid = t?.id ?? t?.tenantId ?? t?.key;
                if (String(tid) !== String(room.tenantId)) return t;
                const clone = { ...t };
                clone.activeTenancy = { ...(clone.activeTenancy ?? {}), roomNumber: Number(newNumberRaw) };
                return clone;
            });
            persistTenants(updatedTenants);
        }
    };

    // helper to display room label
    const roomLabel = (r) => {
        if (r.roomType === "Bedroom") {
            return `Bedroom ${r.roomNumber ?? "?"}`;
        }
        return r.roomType;
    };

    // duplicate check for add button (only when user provided a valid number)
    const isBedroomType = selectedType === "Bedroom";
    const duplicateBedroom = isBedroomType && isValidRoomNumber(selectedRoomNumber) && isDuplicateNumber(selectedRoomNumber);
    const bedroomMissingInputs = isHMO
        ? (isBedroomType && !isValidRoomNumber(selectedRoomNumber) && !selectedTenantId)
        : (isBedroomType && !isValidRoomNumber(selectedRoomNumber));
    const addDisabled = !selectedType || (isBedroomType && (bedroomMissingInputs || duplicateBedroom));

    // when tenant selection changes in add form, pre-fill room number if tenant has one and user hasn't entered a number
    const onSelectTenantForAdd = (tenantId) => {
        setSelectedTenantId(tenantId);
        if (tenantId) {
            const t = findTenantById(tenantId);
            const tn = tenantTenancyRoomNumber(t);
            // default to tenant tenancy number if present, otherwise explicit null
            setSelectedRoomNumber(tn !== undefined ? String(tn) : null);
        } else {
            setSelectedRoomNumber(null);
        }
    };

    // sort rooms for display: bedrooms with numeric roomNumber first in numeric order,
    // then other rooms (sorted by type then id for determinism)
    const sortedRooms = [...rooms].sort((a, b) => {
        const aHas = isValidRoomNumber(a?.roomNumber);
        const bHas = isValidRoomNumber(b?.roomNumber);
        if (aHas && bHas) return Number(a.roomNumber) - Number(b.roomNumber);
        if (aHas && !bHas) return -1;
        if (!aHas && bHas) return 1;
        // neither has a valid room number — group by type then id
        if ((a.roomType || "") !== (b.roomType || "")) return (a.roomType || "").localeCompare(b.roomType || "");
        return String(a.id ?? "").localeCompare(String(b.id ?? ""));
    });

    return (
        <div className="tab-pane fade" id="nav-pills-tab-rooms">
            <h3 className="mt-10px">Rooms</h3>

            <div key={"Rooms"}>
                <div className="card mb-3">
                    <div className="card-body">
                        <div className="row g-2 align-items-start">
                            <div className="col-4">
                                <label className="form-label">Type</label>
                                <select className="form-select" value={selectedType} onChange={e => { setSelectedType(e.target.value); setAddError(null); }}>
                                    <option value="">Select room type</option>
                                    {ROOM_TYPES.sort().map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>

                            {selectedType === "Bedroom" &&
                                <>
                                    <div className="col-3">
                                        <label className="form-label">Room Number</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Enter room number"
                                            value={selectedRoomNumber ?? ""}
                                            onChange={e => { setSelectedRoomNumber(e.target.value === "" ? null : e.target.value); setAddError(null); }}
                                            min="1"
                                        />

                                        {/* render validation in normal flow so it pushes the following content down */}
                                        {(!isValidRoomNumber(selectedRoomNumber) && selectedRoomNumber !== null && selectedRoomNumber !== "") ? (
                                            <div className="form-text text-danger mt-1">Enter a valid positive integer for room number.</div>
                                        ) : duplicateBedroom ? (
                                            <div className="form-text text-danger mt-1">That room number is already used.</div>
                                        ) : null}
                                    </div>

                                    {isHMO && (
                                        <div className="col-4">
                                            <label className="form-label">Assign Tenant</label>
                                            <select className="form-select" value={selectedTenantId} onChange={e => { onSelectTenantForAdd(e.target.value); setAddError(null); }}>
                                                <option value="">Unassigned</option>
                                                {tenantOptions.length === 0 && tenantsLoading && <option value="" disabled>Loading tenants...</option>}
                                                {tenantOptions.length === 0 && !tenantsLoading && <option value="" disabled>No tenants available</option>}
                                                {tenantOptions.map(opt => <option key={opt.id ?? opt.label} value={opt.id ?? ""}>{opt.label}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </>
                            }

                            <div className="col-1 d-grid">
                                <label className="form-label invisible">Add</label>
                                <button className="btn btn-primary" onClick={addRoom} disabled={addDisabled}>
                                    <i className="fa fa-plus me-1" /> Add
                                </button>
                            </div>

                            {/* add form error */}
                            {addError && (
                                <div className="col-12">
                                    <div className="form-text text-danger mt-2">{addError}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <h5 className="card-title">Existing Rooms</h5>

                        {rooms.length === 0 && <div className="text-muted">No rooms added yet.</div>}

                        {rooms.length > 0 && (
                            <ul className="list-group">
                                {sortedRooms.map((r, idx) => {
                                    const assignedTenant = tenants.find(t => {
                                        const id = t.id ?? t.tenantId ?? t.key;
                                        return id && String(id) === String(r.tenantId);
                                    });
                                    return (
                                        <li
                                            className="list-group-item d-flex justify-content-between align-items-start align-items-md-center flex-wrap flex-md-nowrap gap-2"
                                            key={resolveRoomId(r) ?? r.roomNumber ?? `room-${idx}-${r.roomType}`}
                                        >
                                            <div>
                                                <strong style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => goToRoom(r)}>{roomLabel(r)}</strong>
                                                {r.roomType === "Bedroom" && (
                                                    <div className="mt-1">
                                                        <small className="text-muted">Assigned: {assignedTenant ? tenantDisplayName(assignedTenant) : "Unassigned"}</small>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-end">
                                                {r.roomType === "Bedroom" && (
                                                    <>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                style={{ width: 90 }}
                                                                min="1"
                                                                value={r.roomNumber ?? ""}
                                                                onChange={e => updateRoomNumber(r.id, e.target.value)}
                                                            />
                                                            {roomErrors[r.id] && <div className="form-text text-danger">{roomErrors[r.id]}</div>}
                                                        </div>
                                                        {isHMO && (
                                                            <select
                                                                className="form-select form-select-sm"
                                                                style={{ minWidth: 160 }}
                                                                value={r.tenantId ?? ""}
                                                                onChange={e => changeRoomTenant(r.id, e.target.value)}
                                                            >
                                                                <option value="">Unassigned</option>
                                                                {tenantOptions.length === 0 && tenantsLoading && <option value="" disabled>Loading tenants...</option>}
                                                                {tenantOptions.length === 0 && !tenantsLoading && <option value="" disabled>No tenants available</option>}
                                                                {tenantOptions.map(opt => <option key={opt.id ?? opt.label} value={opt.id ?? ""}>{opt.label}</option>)}
                                                            </select>
                                                        )}
                                                    </>
                                                )}
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => removeRoom(r.id)}>
                                                    <i className="fa fa-times" />
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}